import { useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/reui/alert';
import { useMe } from '@/hooks/use-me';
import { FranjaPiloto } from '@/components/layout/franja-piloto';

// Los 4 cuadrados del logo de Microsoft, hardcodeados (igual RISIT) — no
// vale la pena traer un ícono de librería para esto.
function MicrosoftLogo() {
  return (
    <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

function mensajeError(error: string | null): string | null {
  if (error === 'unauthorized') {
    return 'Tu usuario no está autorizado. Pedile acceso a Administración.';
  }
  if (error === 'disabled') {
    return 'Tu acceso está desactivado. Hablá con Administración.';
  }
  if (error) {
    return 'No se pudo iniciar sesión. Probá de nuevo.';
  }
  return null;
}

// Login de un solo botón: el flujo OAuth necesita un redirect full-page, por
// eso el Button se renderiza como <a> real (polimorfismo de Base UI) en vez
// de un onClick con fetch.
export function Login() {
  const { data: user, isLoading } = useMe();
  const [searchParams] = useSearchParams();
  const error = mensajeError(searchParams.get('error'));

  useEffect(() => {
    document.title = 'Ingresar — RedeApp';
    return () => {
      document.title = 'RedeApp — Ilubaires';
    };
  }, []);

  if (isLoading) return null;
  if (user) return <Navigate to="/" replace />;

  return (
    <div className="flex min-h-screen flex-col bg-surface-page">
      {/* Franja del piloto 4.0.0 (solo con MODO_PILOTO=1): /api/health es
          público, así que se ve desde el login. */}
      <FranjaPiloto />
      <div className="flex flex-1 items-center justify-center px-4">
        <div className="w-full max-w-sm rounded-xl border border-border bg-card p-8 text-center shadow-sm">
          <img
            src="/ilubaires-logo.svg"
            alt="Ilubaires"
            className="mx-auto h-14 w-auto select-none dark:hidden"
            draggable={false}
          />
          <img
            src="/ilubaires-logo-dark.svg"
            alt="Ilubaires"
            className="mx-auto hidden h-14 w-auto select-none dark:block"
            draggable={false}
          />

          <h1 className="mt-6 text-lg font-semibold text-foreground">RedeApp</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Gestión de redeterminaciones de precios por obra
          </p>

          {error && (
            // Alert trae role="alert" fijo — correcto acá, es un error
            // (regla común §1.12).
            <Alert variant="destructive" className="mt-4 text-left">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button
            size="lg"
            className="mt-6 w-full gap-2"
            render={<a href="/api/auth/login" />}
            nativeButton={false}
          >
            <MicrosoftLogo />
            Ingresar con Microsoft
          </Button>
        </div>
      </div>
    </div>
  );
}
